import { Radar, AlertTriangle, RefreshCw, ChevronDown, ChevronRight, Clock, Sparkles, Wind } from 'lucide-react';
import { useState } from 'react';
import { useWeatherStore } from '@/stores/weatherStore';
import { getSeverityHexColor, SEVERITY_LEVELS } from '@/lib/weather/colors';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { AlertSeverity } from '@/types/weather';

function Section({
  icon: Icon,
  title,
  enabled,
  expanded,
  onToggleExpand,
  badge,
  loading,
  children,
}: {
  icon: React.ElementType;
  title: string;
  enabled?: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  badge?: React.ReactNode;
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Icon size={14} />
          {title}
        </div>
        <div className="flex items-center gap-2">
          {loading && <RefreshCw size={11} className="animate-spin text-muted-foreground" />}
          {badge}
          {enabled !== undefined && (
            <div className={cn('w-2 h-2 rounded-full border', enabled ? 'bg-emerald-400 border-emerald-400' : 'bg-transparent border-muted-foreground/50')} />
          )}
        </div>
      </button>
      {expanded && <div className="px-3 pb-3 space-y-3">{children}</div>}
    </div>
  );
}

export function WeatherPanel() {
  const {
    radarEnabled, radarOpacity, radarTimeOffset, radarTimeOptions,
    lightPollutionEnabled, lightPollutionOpacity,
    windEnabled, windOpacity, windForecastHour, isLoadingWind,
    alertsEnabled, alerts, isLoadingAlerts, alertsError, lastAlertsFetch,
    setRadarEnabled, setRadarOpacity, setRadarTimeOffset, commitRadarTime, refreshRadarTimeOptions,
    setLightPollutionEnabled, setLightPollutionOpacity,
    setWindEnabled, setWindOpacity, setWindForecastHour, commitWindTime,
    setAlertsEnabled, fetchAlerts,
  } = useWeatherStore();

  const [radarExp, setRadarExp] = useState(true);
  const [windExp, setWindExp] = useState(true);
  const [lightExp, setLightExp] = useState(false);
  const [alertsExp, setAlertsExp] = useState(true);

  const alertCounts = alerts.reduce(
    (acc, alert) => {
      const s = alert.properties?.severity || 'Unknown';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    {} as Record<AlertSeverity, number>
  );

  const formatLastFetch = () => {
    if (!lastAlertsFetch) return 'Never';
    const diff = Date.now() - lastAlertsFetch;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return `${Math.floor(diff / 3600000)}h ago`;
  };

  return (
    <div className="p-3 space-y-2">
      {/* Radar */}
      <Section icon={Radar} title="NEXRAD Radar" enabled={radarEnabled} expanded={radarExp} onToggleExpand={() => setRadarExp(!radarExp)}>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Show Radar</span>
          <Switch checked={radarEnabled} onCheckedChange={setRadarEnabled} />
        </div>
        {radarEnabled && (
          <>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Opacity</span>
                <span>{Math.round(radarOpacity * 100)}%</span>
              </div>
              <Slider value={[radarOpacity * 100]} onValueChange={(v) => setRadarOpacity((Array.isArray(v) ? v[0] : v) / 100)} max={100} step={1} />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-1"><Clock size={11} /> Time</div>
                <span className="font-medium text-foreground">
                  {radarTimeOptions.find((o) => o.value === radarTimeOffset)?.label || 'Live'}
                </span>
              </div>
              <Slider
                value={[radarTimeOffset + 120]}
                onValueChange={(v) => { const val = Array.isArray(v) ? v[0] : v; setRadarTimeOffset(val - 120); }}
                onValueCommitted={() => commitRadarTime()}
                onPointerDown={() => refreshRadarTimeOptions()}
                max={120} step={5}
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>-2h</span><span>Live</span>
              </div>
            </div>
          </>
        )}
        <p className="text-[10px] text-muted-foreground">
          NEXRAD precipitation radar. {radarTimeOffset === 0 ? 'Auto-refreshes every 5 min.' : 'Showing historical data.'}
        </p>
      </Section>

      {/* Wind */}
      <Section icon={Wind} title="Wind" enabled={windEnabled} expanded={windExp} onToggleExpand={() => setWindExp(!windExp)} loading={isLoadingWind}>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Show Wind</span>
          <Switch checked={windEnabled} onCheckedChange={setWindEnabled} />
        </div>
        {windEnabled && (
          <>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Opacity</span>
                <span>{Math.round(windOpacity * 100)}%</span>
              </div>
              <Slider value={[windOpacity * 100]} onValueChange={(v) => setWindOpacity((Array.isArray(v) ? v[0] : v) / 100)} max={100} step={1} />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-1"><Clock size={11} /> Forecast</div>
                <span className="font-medium text-foreground">{windForecastHour === 0 ? 'Now' : `+${windForecastHour}h`}</span>
              </div>
              <Slider
                value={[windForecastHour]}
                onValueChange={(v) => setWindForecastHour(Array.isArray(v) ? v[0] : v)}
                onValueCommitted={() => commitWindTime()}
                max={48} step={3}
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>Now</span><span>+48h</span>
              </div>
            </div>
            <div className="pt-2 border-t border-border">
              <p className="text-[10px] text-muted-foreground mb-1.5">Wind Speed (m/s)</p>
              <div
                className="h-1.5 rounded-full"
                style={{
                  background: 'linear-gradient(to right, #6271B7, #3961A0, #4A94AA, #4DA791, #55A764, #7EAF4B, #B8B73F, #E3B038, #F18A36, #EA5D3B, #D53847, #B73779)',
                }}
              />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                <span>0</span><span>15</span><span>30+</span>
              </div>
            </div>
          </>
        )}
        <p className="text-[10px] text-muted-foreground">Animated wind particles. Data from Open-Meteo.</p>
      </Section>

      {/* Light Pollution */}
      <Section icon={Sparkles} title="Light Pollution" enabled={lightPollutionEnabled} expanded={lightExp} onToggleExpand={() => setLightExp(!lightExp)}>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Show Light Pollution</span>
          <Switch checked={lightPollutionEnabled} onCheckedChange={setLightPollutionEnabled} />
        </div>
        {lightPollutionEnabled && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Opacity</span>
              <span>{Math.round(lightPollutionOpacity * 100)}%</span>
            </div>
            <Slider value={[lightPollutionOpacity * 100]} onValueChange={(v) => setLightPollutionOpacity((Array.isArray(v) ? v[0] : v) / 100)} max={100} step={1} />
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">VIIRS satellite imagery showing artificial light at night.</p>
      </Section>

      {/* Alerts */}
      <Section
        icon={AlertTriangle}
        title="Weather Alerts"
        enabled={alertsEnabled}
        expanded={alertsExp}
        onToggleExpand={() => setAlertsExp(!alertsExp)}
        loading={isLoadingAlerts}
        badge={alertsEnabled && alerts.length > 0 ? <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{alerts.length}</Badge> : undefined}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Show Alerts</span>
          <Switch checked={alertsEnabled} onCheckedChange={setAlertsEnabled} />
        </div>

        {alertsEnabled && alertsError && (
          <p className="text-xs text-destructive">{alertsError}</p>
        )}

        {alertsEnabled && !isLoadingAlerts && !alertsError && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>Updated: {formatLastFetch()}</span>
              <button onClick={() => fetchAlerts()} className="p-0.5 rounded hover:bg-accent/50 transition-colors">
                <RefreshCw size={11} />
              </button>
            </div>
            {alerts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No active alerts</p>
            ) : (
              <div className="space-y-1">
                {SEVERITY_LEVELS.map((severity) => {
                  const count = alertCounts[severity] || 0;
                  if (count === 0) return null;
                  return (
                    <div key={severity} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: getSeverityHexColor(severity) }} />
                      <span className="text-foreground">{severity}</span>
                      <span className="text-muted-foreground">({count})</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">NWS alerts for the US. Auto-refreshes every 15 min.</p>
      </Section>
    </div>
  );
}
